import { useEffect, useRef, useState } from "react";
import "./LoginPage.scss";
import CustomInput from "../../components/customInput/CustomInput";
import gsap from "gsap";
import useLogin from "../../hooks/useLogin.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import DownSvg from "../../assets/svg/DownSvg.jsx";
import UpSvg from "../../assets/svg/UpSvg.jsx";
import { IMAGES } from "../../assets/media/images.js";
import { CONFIG } from "../../assets/configs/config.js";
import ModalWindow from "../../components/modalWindow/ModalWindow.jsx";
import TgSticker from "../../components/sticker/TgSticker.jsx";
import { STICKERS } from "../../assets/media/stickers.js";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

function LoginPage() {
    const [login, setLogin] = useState("");
    const [password, setPassword] = useState("");
    const [isClick, setIsClick] = useState(false);
    const [isRenderFinish, setIsRenderFinish] = useState(true);
    const [showError, setShowError] = useState(false);
    const [isOpenInfo, setIsOpenInfo] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImg, setSelectedImg] = useState(false);
    const [sticker, setSticker] = useState(STICKERS.eyes);
    const loginRef = useRef(null);
    const loginErrorRef = useRef(null);
    const loginBoxRef = useRef(null);
    const buttonRef = useRef(null);
    const instructionRef = useRef(null);
    const instructionTextRef = useRef(null);
    const stickerRef = useRef(null);
    const inputRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const loginMutation = useLogin();
    const from = location.state?.from || "/";

    useEffect(() => {
        gsap.fromTo(loginRef.current, {opacity: 0, y: -10, scale: 1.01}, {opacity: 1, y: 0, scale: 1, duration: .5, ease: "power2.out"})
    }, [])

    useEffect(() => {
        gsap.fromTo(stickerRef.current, {opacity: 0, scale: .5}, {opacity: 1, scale: 1, duration: .7, ease: "power2.out"})
    }, [sticker])

    useEffect(() => {
        if (isOpenInfo) {
            gsap.to(instructionRef.current, {height: 750, duration: 1, ease: "power2.out"})
            gsap.fromTo(instructionTextRef.current,
                {opacity: 0, scale: .9, y: -45},
                {opacity: 1, scale: 1, y: 20, duration: 1, ease: "power2.out"}
            )
        } else {
            gsap.to(instructionRef.current, {height: 35, duration: 1, ease: "power2.out"})
            gsap.to(instructionTextRef.current, {opacity: 0, scale: .9, y: -45, duration: .6, ease: "power2.in"})
        }
    }, [isOpenInfo])

    useEffect(() => {
        if (loginMutation.isError) {
            setSticker(STICKERS.dont_know)
            !showError && gsap.to(loginBoxRef.current, {height: 280, duration: .3, ease: "power2.out", onComplete: () => setShowError(true)})
            showError && gsap.fromTo(loginErrorRef.current,
                {y: -10, opacity: 0},
                {y: 0, opacity: 1, duration: .4, ease: "power2.out"}
            )
        }
    }, [loginMutation.isError, showError])

    useEffect(() => {
        if (isClick) {
            gsap.to(buttonRef.current, {scale: .98, opacity: .8, duration: .1, ease: "power2.out", onComplete: () => setIsClick(false)})
        } else {
            gsap.to(buttonRef.current, {scale: 1, opacity: 1, duration: .1, ease: "power2.in", onComplete: () => setIsRenderFinish(true)})
        }
    }, [isClick])

    useEffect(() => {
        if (loginMutation.isSuccess) {
            navigate(from, {replace: true})
        }
    }, [loginMutation, from, navigate])

    const onClickButton = () => {
        if (isRenderFinish) {
            setShowError(false)
            loginMutation.mutate({ login, password })
            setIsRenderFinish(false)
            setIsClick(true)
        }
    }

    const onClickInfo = () => {
        setIsOpenInfo(!isOpenInfo)
        if (!isOpenInfo) {
            const y = instructionRef.current.getBoundingClientRect().top + window.pageYOffset;
            gsap.to(window, {
                scrollTo: y - 150,
                duration: 1,
                ease: "power2.out"
            });
        }
    }

    const onClickInstructionImg = (img) => {
        setSelectedImg(img);
        setIsModalOpen(true);
    }

    return (
        <div className="login-page-wrapper" ref={loginRef}>
            <ModalWindow 
                key="login-modal"
                open={isModalOpen}
                setOpen={setIsModalOpen}
                description="Изображение"
                children={<img src={selectedImg} alt="Img" />}
            />

            <div className="login-wrapper">
                <div className="login-description">Авторизация</div>

                <div className="login-sticker-wrapper" ref={stickerRef}>
                    <div className="login-sticker"><TgSticker src={sticker} loop={true} /></div>
                </div>
                
                <div className="login-block" ref={loginBoxRef}>
                    <div className="login-inputs-block">
                        <div className="login-input" ref={inputRef}>
                            <CustomInput
                                label="Логин"
                                placeholder="Telegram id or username..."
                                idText="inputLogin"
                                onChange={setLogin}
                                onFocus={() => setSticker(STICKERS.keyboard)}
                            />
                        </div>

                        <div className="login-input" ref={inputRef}>
                            <CustomInput
                                label="Пароль"
                                placeholder="Password..."
                                idText="inputPassword"
                                onChange={setPassword}
                                onFocus={() => setSticker(STICKERS.keyboard)}
                                onFunc={onClickButton}
                            />
                        </div>

                        {showError && loginMutation.isError && (loginMutation.error.response.data.error === "invalid login or password" ? 
                        <div className="login-error" ref={loginErrorRef}>Неверный логин или пароль.</div> :
                        <div className="login-error" ref={loginErrorRef}>Произошла ошибка.</div>)}
                    </div>
                    <div className="login-button" ref={buttonRef} onClick={onClickButton}>Войти</div>
                </div>
            </div>

            <div className="info-instruction-wrapper" ref={instructionRef}>
                <div className="info-wrapper">
                    <div className="info-text">
                        Нет аккаунта? Зарегистрируй его в нашем телеграм боте.
                        <div className="instruction-button" onClick={onClickInfo}>
                            Инструкция
                            {!isOpenInfo ? <DownSvg /> : <UpSvg />}
                        </div>
                    </div>
                </div> 

                <div className={"instruction-wrapper" + (isOpenInfo ? " open" : "")}>
                    <div className="instruction-text" ref={instructionTextRef}>
                        <span>
                            {"1. Для начала перейди в "}
                            <a href={CONFIG.bot_link} target="_blank" rel="noopener noreferrer">бота</a>
                            {" и нажми кнопку "}
                            <span className="instruction-select-text">«Старт»</span>
                            {". Всё, ты зарегистрирован."}
                        </span>
                        <img src={IMAGES.instruction_start} alt="Start" onClick={() => onClickInstructionImg(IMAGES.instruction_start)} />

                        <span>
                            {"2. Чтобы иметь доступ к аккаунту с сайта, нужно установить пароль в боте. Для этого напиши боту команду "}
                            <span className="instruction-select-text">«Установить пароль [пароль]»</span>{"."}
                        </span>
                        <img src={IMAGES.instruction_password} alt="Password" onClick={() => onClickInstructionImg(IMAGES.instruction_password)} />

                        <span>
                            {"3. Теперь можешь использовать этот пароль для входа в аккаунт на сайте. Логином будет твой "} 
                            <span className="instruction-select-text">Telegram ID</span>
                            {" или "}
                            <span className="instruction-select-text">username</span>
                            {". Айди можно посмотореть, написав боту команду "}
                            <span className="instruction-select-text">«Профиль»</span>{"."}
                        </span>
                        <img src={IMAGES.instruction_profile} alt="Profile" onClick={() => onClickInstructionImg(IMAGES.instruction_profile)} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LoginPage;